import type { FeatureFlagsDevToolsModel } from '.';
import { launchDarklyFlagUrl } from './launchDarklyFlagUrl';
import { signal } from '@preact/signals-react';
import type { ConfigService } from '@thinkalpha/ui-ioc/core/types/ConfigService';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import { inject, injectable } from 'src/features/ioc';
import { createPersistedPanelRect } from 'src/lib/persistedPanelRect';
import type { PersistedPanelRectProps } from 'src/lib/persistedPanelRect';
import type { SyncStorageService } from 'src/services/SyncStorageService';

const STORAGE_KEY = 'featureFlagsDevTools.isOpen';
const PANEL_RECT_STORAGE_KEY = 'featureFlagsDevTools.widgetState';
const PANEL_DEFAULT_SIZE = { width: 320, height: 400 };
const PANEL_MIN_SIZE = { width: 280, height: 250 };

function loadOpenState(): boolean {
    try {
        return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
}

function saveOpenState(isOpen: boolean): void {
    try {
        localStorage.setItem(STORAGE_KEY, String(isOpen));
    } catch {
        // Ignore storage errors
    }
}

@injectable()
export class FeatureFlagsDevToolsModelImpl implements FeatureFlagsDevToolsModel {
    readonly #logger: Logger;
    readonly #isOpen;
    readonly #configService: ConfigService;
    readonly #storage: SyncStorageService;

    constructor(
        @inject('Logger') logger: Logger,
        @inject('ConfigService') configService: ConfigService,
        @inject('SyncStorageService') storage: SyncStorageService,
    ) {
        this.#logger = logger.getSubLogger({ name: 'FeatureFlagsDevToolsModel' });
        this.#configService = configService;
        this.#storage = storage;
        const initialOpenState = loadOpenState();
        if (TRACE) {
            this.#logger.trace('Loaded initial open state', { isOpen: initialOpenState });
        }
        this.#isOpen = signal(initialOpenState);
    }

    get isOpen() {
        return this.#isOpen;
    }

    toggleOpen(): void {
        this.#isOpen.value = !this.#isOpen.value;
        if (TRACE) {
            this.#logger.trace('Toggled open state', { isOpen: this.#isOpen.value });
        }
        saveOpenState(this.#isOpen.value);
    }

    close(): void {
        this.#isOpen.value = false;
        if (TRACE) {
            this.#logger.trace('Closed devtools', { isOpen: false });
        }
        saveOpenState(false);
    }

    launchDarklyFlagUrl(flagKey: string): string {
        return launchDarklyFlagUrl(flagKey, this.#configService.launchDarklyConfig.environmentKey);
    }

    loadPanelRect(): PersistedPanelRectProps {
        return createPersistedPanelRect({
            storage: this.#storage,
            log: this.#logger,
            storageKey: PANEL_RECT_STORAGE_KEY,
            defaultSize: PANEL_DEFAULT_SIZE,
            minSize: PANEL_MIN_SIZE,
        });
    }
}
